import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { History, TrendingUp, TrendingDown, Calendar, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { resolveBackendUrl } from '../lib/utils';

const BACKEND_URL = resolveBackendUrl();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const HistoryPage = () => {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/predictions/history?limit=100`);
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error('Fetch history error:', error);
      toast.error('Failed to load prediction history');
    } finally {
      setLoading(false);
    }
  };

  const filteredPredictions = predictions.filter(pred =>
    pred.asteroid_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const getRiskBadge = (risk) => {
    const colors = {
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      high: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[risk.toLowerCase()] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const stats = {
    total: predictions.length,
    profitable: predictions.filter(p => p.is_profitable).length,
    avgROI: predictions.length > 0
      ? (predictions.reduce((sum, p) => sum + p.predicted_roi, 0) / predictions.length).toFixed(1)
      : 0,
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-teal-600 flex items-center justify-center">
              <History className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold gradient-text mb-3" data-testid="page-title">Prediction History</h1>
          <p className="text-gray-400" data-testid="page-description">Past asteroid profitability predictions</p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="history-stats">
          <Card className="glass-card border-white/10">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold gradient-text mb-2" data-testid="stat-total">{stats.total}</div>
              <div className="text-gray-400 text-sm">Total Predictions</div>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/10">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-green-400 mb-2" data-testid="stat-profitable">{stats.profitable}</div>
              <div className="text-gray-400 text-sm">Profitable Asteroids</div>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/10">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-cyan-400 mb-2" data-testid="stat-avg-roi">{stats.avgROI}%</div>
              <div className="text-gray-400 text-sm">Average ROI</div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="search-input"
              placeholder="Search by asteroid name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>

        {/* Predictions List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="glass-card border-white/10" data-testid="history-loading">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="loading-spinner mb-4"></div>
                <p className="text-gray-500">Loading predictions...</p>
              </CardContent>
            </Card>
          ) : filteredPredictions.length > 0 ? (
            filteredPredictions.map((prediction, index) => (
              <motion.div
                key={prediction.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                data-testid={`prediction-${index}`}
              >
                <Card className="glass-card border-white/10 hover:border-cyan-500/30 transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-white text-lg" data-testid={`prediction-${index}-name`}>
                          {prediction.asteroid_name}
                        </CardTitle>
                        <CardDescription className="text-gray-400 text-xs flex items-center gap-1 mt-1">
                          <Calendar className="w-3 h-3" />
                          {formatTimestamp(prediction.timestamp)}
                        </CardDescription>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {prediction.is_profitable ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30" data-testid={`prediction-${index}-status`}>
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Profitable
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30" data-testid={`prediction-${index}-status`}>
                            <TrendingDown className="w-3 h-3 mr-1" />
                            Not Profitable
                          </Badge>
                        )}
                        <Badge className={getRiskBadge(prediction.risk_level)} data-testid={`prediction-${index}-risk`}>
                          {prediction.risk_level} Risk
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="p-3 rounded-lg bg-white/5" data-testid={`prediction-${index}-probability`}>
                        <div className="text-xs text-gray-400 mb-1">Probability</div>
                        <div className="text-lg font-bold text-cyan-400">
                          {(prediction.probability * 100).toFixed(1)}%
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5" data-testid={`prediction-${index}-roi`}>
                        <div className="text-xs text-gray-400 mb-1">Predicted ROI</div>
                        <div className={`text-lg font-bold ${prediction.predicted_roi > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {prediction.predicted_roi > 0 ? '+' : ''}{prediction.predicted_roi}%
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5" data-testid={`prediction-${index}-risk-level`}>
                        <div className="text-xs text-gray-400 mb-1">Risk Level</div>
                        <div className="text-lg font-bold text-white">
                          {prediction.risk_level}
                        </div>
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5" data-testid={`prediction-${index}-recommendation`}>
                      <div className="text-xs text-gray-400 mb-1">Recommendation</div>
                      <p className="text-sm text-gray-300">{prediction.recommendation}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          ) : (
            <Card className="glass-card border-white/10" data-testid="history-empty">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <History className="w-16 h-16 text-gray-600 mb-4" />
                <p className="text-gray-500 text-center">
                  {searchTerm ? 'No predictions found matching your search' : 'No prediction history available yet'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;
